import { requiredField } from '../../../../service/validation'

/**
 * Creates a validator for the required address fields with the given error messages
 * @param {{ invalidAddressMessage: string, outOfServiceAddressMessage: string }} errorMessages
 * @param {string} key
 * @returns {function}
 */
const requiredAddressField = (errorMessages, key) => values => {
  const { invalidAddressMessage, outOfServiceAddressMessage } = errorMessages
  const hasAttributes =
    values &&
    values[key] &&
    values[key].address &&
    values[key].latitude &&
    values[key].longitude
  const geoAddress =
    hasAttributes && values[key].latitude && values[key].longitude
  const createErrorObject = message => ({
    [key]: {
      address: message,
      latitude: message,
      longitude: message,
    },
  })

  if (!hasAttributes) {
    return createErrorObject(invalidAddressMessage)
  } else if (
    geoAddress === 'Location out of range' ||
    geoAddress === 'Location not in service'
  ) {
    return createErrorObject(outOfServiceAddressMessage)
  }
}

const requirePackageContent = (
  errorMessage,
  packageContentReduxFormName
) => value => {
  const packageContent = value[packageContentReduxFormName]
  return {
    [packageContentReduxFormName]: requiredField(errorMessage)(packageContent),
  }
}

const charLimit100Field = errorMessage => value => {
  return value ? (value.length <= 100 ? undefined : errorMessage) : undefined
}

const charLimit250Field = errorMessage => value => {
  return value ? (value.length <= 250 ? undefined : errorMessage) : undefined
}

const charLimit100PackageContent = (
  errorMessage,
  packageContentReduxFormName
) => value => {
  const packageContent = value[packageContentReduxFormName]
  const hasPackageContent = !!packageContent
  return {
    [packageContentReduxFormName]: hasPackageContent
      ? charLimit100Field(errorMessage)(packageContent)
      : undefined,
  }
}

const _isPositiveInteger = value =>
  !Number.isNaN(parseInt(value, 10)) &&
  !value.includes('.') &&
  parseInt(value, 10) > 0
const _lessThan120 = value => !Number.isNaN(parseInt(value, 10)) && value <= 120

const requirePackageSizeSeparateLWH = ({
  overSizedError,
  oneSideOverSizedError,
  invalidError,
}) => ({ length, width, height }) => {
  const anySideZeroOrNotFilled = !(
    _isPositiveInteger(width) &&
    _isPositiveInteger(length) &&
    _isPositiveInteger(height)
  )
  const totalSizeOver180 =
    parseInt(width, 10) + parseInt(length, 10) + parseInt(height, 10) > 180
  const anySideOver120 =
    parseInt(width, 10) > 120 ||
    parseInt(length, 10) > 120 ||
    parseInt(height, 10) > 120

  if (anySideZeroOrNotFilled) {
    return {
      package_size_error: invalidError,
      length: _isPositiveInteger(length) ? undefined : ' ',
      width: _isPositiveInteger(width) ? undefined : ' ',
      height: _isPositiveInteger(height) ? undefined : ' ',
    }
  } else if (totalSizeOver180) {
    return {
      package_size_error: overSizedError,
      length: ' ',
      width: ' ',
      height: ' ',
    }
  } else if (anySideOver120) {
    return {
      package_size_error: oneSideOverSizedError,
      length: _lessThan120(length) ? undefined : ' ',
      width: _lessThan120(width) ? undefined : ' ',
      height: _lessThan120(height) ? undefined : ' ',
    }
  }
}

const requirePackageWeightWithLimit = ({
  overWeightError,
  invalidError,
}) => value => {
  const overWeight = _isPositiveInteger(value.weight) && value.weight > 20
  const error = { weight: overWeight ? overWeightError : invalidError }

  return !_isPositiveInteger(value.weight) || overWeight ? error : undefined
}

const requireName = (errorMessage, key) => value => {
  return {
    [key]: {
      name: requiredField(errorMessage)(value[key] && value[key].name),
    },
  }
}

const verifyPaymentOptions = ({ cardError, walletError }) => value => {
  const {
    payment_type: paymentType,
    hasCreditCard,
    hasSufficientBalance,
  } = value

  switch (paymentType) {
    case 'credit_card':
      return hasCreditCard ? undefined : { hasCreditCard: cardError }
    case 'prepaid_wallet':
      return hasSufficientBalance
        ? undefined
        : { hasSufficientBalance: walletError }
    default:
      return undefined
  }
}

export {
  requiredAddressField,
  charLimit250Field,
  requirePackageContent,
  charLimit100PackageContent,
  requirePackageSizeSeparateLWH,
  requirePackageWeightWithLimit,
  requireName,
  verifyPaymentOptions,
}
